import {
  AutocompleteInput,
  DateInput,
  Modal,
  NumberInput,
  SelectInput,
  SwitchInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { InfoOutlined } from '@mui/icons-material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { ShopItemCategory, shopItemCategories } from 'types/MarketplaceItems';
import { useCreateEmployeeExpense, useEmployees } from 'store/employees';
import { usePredefinedToasts } from 'utils/Toast';
import { Stack, Tooltip } from '@mui/material';
import { ExpenseStatus, expenseStatuses } from 'store/expenses';
import { useEmployeeBenefits } from 'store/benefits';
import { useDebouncedCallback } from 'use-debounce';

interface Values {
  employee: string;
  date: string;
  status: ExpenseStatus;
  benefits: string[];
  useRecognition: boolean;
  categories: ShopItemCategory[];
  amount: number;
  description: string;
}

const INITIAL_VALUES: Values = {
  employee: '',
  date: '',
  status: '' as ExpenseStatus,
  benefits: [],
  useRecognition: false,
  categories: [],
  amount: NaN,
  description: '',
};

interface Props extends ModalProps {
  employeeId?: string;
}

export const CreateExpenseModal: FC<Props> = ({ employeeId, closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const [employeesSearchText, setEmployeesSearchText] = useState('');

  const { control, handleSubmit, setValue, watch } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const [benefits, employee, useRecognition] = watch([
    'benefits',
    'employee',
    'useRecognition',
  ]);

  const { data: benefitOptions, isInitialLoading: isBenefitsOptionsLoading } =
    useEmployeeBenefits(employeeId || employee, { trackBalance: true });
  const { data: employeeOptions } = useEmployees({
    search: employeesSearchText,
  });

  const handleEmployeesSearch = useDebouncedCallback(
    setEmployeesSearchText,
    300,
  );

  const { mutate: createEmployeeExpense, isLoading: isCreating } =
    useCreateEmployeeExpense();

  const onSubmit: SubmitHandler<Values> = ({
    employee,
    date,
    status,
    benefits,
    useRecognition,
    categories,
    amount,
    description,
  }) => {
    createEmployeeExpense(
      {
        employeeId: employeeId || employee,
        expense: {
          date,
          status,
          amount,
          benefits,
          recognition: useRecognition,
          shopCategories: categories,
          description,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.added();
          closeModal({ action: 'CLOSE' });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('actions.on_specific_date')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isCreating,
          },
        ]}
      >
        <Stack gap="20px">
          {employeeId ? null : (
            <Controller
              name="employee"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => {
                return (
                  <AutocompleteInput
                    label={t('entity_type.employee')}
                    name={name}
                    value={value}
                    multiple={false}
                    required
                    onChange={(v) => {
                      onChange(v);
                      setValue('benefits', INITIAL_VALUES.benefits);
                    }}
                    options={
                      employeeOptions?.items.map((employee) => ({
                        label: employee.fullName,
                        value: employee.id,
                      })) ?? []
                    }
                    onInputChange={handleEmployeesSearch}
                    ref={ref}
                  />
                );
              }}
            />
          )}

          <Controller
            name="status"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SelectInput
                  label={t('common.status')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={expenseStatuses
                    .filter((status) => ['completed', 'paid'].includes(status))
                    .map((status) => ({
                      label: t(`expenses.status.${status}`),
                      value: status,
                    }))}
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="date"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateInput
                label={t('common.date')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="benefits"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <Stack gap={1}>
                  <Typography variant="p1" color="textSecondary">
                    <Stack flexDirection="row" gap={1} alignItems="center">
                      {t('wallet.employeeExpenseFundsSourceSectionTitle')}
                      <Tooltip
                        title={t(
                          'wallet.employeeExpenseFundsSourceSectionHint',
                        )}
                      >
                        <InfoOutlined fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Typography>

                  <AutocompleteInput
                    label={t('wallet.employeeExpenseFundsSource')}
                    name={name}
                    value={value}
                    multiple
                    disabled={
                      (!employeeId && !employee) || isBenefitsOptionsLoading
                    }
                    required={!useRecognition}
                    onChange={onChange}
                    options={
                      benefitOptions?.map((benefit) => ({
                        label: benefit.name,
                        value: benefit.id,
                      })) ?? []
                    }
                    ref={ref}
                  />
                </Stack>
              );
            }}
          />

          <Controller
            name="useRecognition"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SwitchInput
                  label={t('wallet.employeeExpenseSourceRecognition')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required={!benefits.length}
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="categories"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <AutocompleteInput
                  label={t('wallet.employeeExpenseItemCtegory')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={shopItemCategories.map((category) => ({
                    label: t(`marketplaceItems.itemCategory-${category}`),
                    value: category,
                  }))}
                  multiple
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="amount"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('benefitExpenses.expenseAmount')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                ref={ref}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('benefitExpenses.expenseDescription')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
